import { useEffect, useState } from 'react'
import Modal from './Modal'
import { reportsApi } from '../lib/api'

export const REPORT_CATEGORY_OPTIONS = [
  { value: 'SCAM_PHISHING', label: 'Scam or phishing', help: 'Asks for money, codes, passwords or details, or has suspicious links' },
  { value: 'FAKE_LISTING', label: 'Not a real rental', help: 'The item does not exist, or is not the item shown' },
  { value: 'OFF_PLATFORM', label: 'Wants to deal or pay outside igotthatus.com', help: 'Asks for cash, Venmo, gift cards, or to move the chat elsewhere' },
  { value: 'SPAM', label: 'Spam or advertising', help: 'Promotes something else, or repeated unwanted posts' },
  { value: 'INAPPROPRIATE', label: 'Inappropriate or illegal content', help: 'Adult, violent or illegal content or items' },
  { value: 'HARASSMENT', label: 'Harassment or discrimination', help: 'Threats, abuse or unfair treatment' },
  { value: 'UNSAFE_ITEM', label: 'Unsafe or prohibited item', help: 'Dangerous, recalled or not allowed on the site' },
  { value: 'OTHER', label: 'Something else', help: 'Tell us what is wrong' },
]

interface Props {
  isOpen: boolean
  targetType: 'LISTING' | 'USER'
  targetId: string
  targetLabel: string
  onClose: () => void
}

// "Report" for a listing or a person: pick what is wrong, add details, and our team reviews it.
export default function ReportDialog({ isOpen, targetType, targetId, targetLabel, onClose }: Props) {
  const [category, setCategory] = useState('')
  const [details, setDetails] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)

  useEffect(() => {
    if (isOpen) { setCategory(''); setDetails(''); setError(''); setSent(false) }
  }, [isOpen])

  const submit = async () => {
    setSending(true)
    setError('')
    try {
      await reportsApi.create({ targetType, targetId, category, details: details.trim() || undefined })
      setSent(true)
    } catch (err: any) {
      setError(err?.response?.data?.error || 'We could not send your report. Please try again.')
    } finally {
      setSending(false)
    }
  }

  const help = REPORT_CATEGORY_OPTIONS.find((o) => o.value === category)?.help
  const needsDetails = category === 'OTHER'

  if (sent) {
    return (
      <Modal isOpen={isOpen} title="Thank you" confirmLabel="Done" cancelLabel="Close" onConfirm={onClose} onCancel={onClose}>
        <p>Your report has been sent to our team. We review every report, and we may contact you if we need more information. If anyone is in danger, call 911.</p>
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} title={`Report ${targetLabel}`} confirmLabel={sending ? 'Sending…' : 'Send report'} confirmVariant="danger" disabled={sending} confirmDisabled={!category || (needsDetails && !details.trim())} onCancel={() => !sending && onClose()} onConfirm={submit}>
      <div className="space-y-4">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
        <label className="block text-sm font-medium text-[var(--text-primary)]">What is the problem?
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full mt-1 px-3 min-h-[44px] border border-[var(--border)] rounded-lg bg-white">
            <option value="">Choose one…</option>
            {REPORT_CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {help && <span className="block text-xs text-[var(--text-muted)] mt-1 font-normal">{help}</span>}
        </label>
        <label className="block text-sm font-medium text-[var(--text-primary)]">Details {needsDetails ? '' : '(optional)'}
          <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={4} maxLength={2000} placeholder="What happened? Include anything that helps us check." className="w-full mt-1 px-3 py-2 border border-[var(--border)] rounded-lg" />
        </label>
        <p className="text-xs text-[var(--text-muted)]">This goes to our team, not to {targetType === 'USER' ? 'the person' : 'the owner'}. To dispute a rental you are part of, use "Report a problem" on the rental.</p>
      </div>
    </Modal>
  )
}
